import { Controller } from "@hotwired/stimulus"

// Axle 1 — Canon Expression & Patrol System
// Axle is a mascot/promo, NOT a clickable link. Chat button is separate.
export default class extends Controller {
  static targets = ["image"]
  static values = {
    state: { type: String, default: "idle" },
    idleUrl: String,
    listeningUrl: String,
    thinkingUrl: String,
    suggestingUrl: String,
    successUrl: String,
    warningUrl: String,
    walkLeftUrl: String,
    walkLeft2Url: String,
    walkLeftMidUrl: String,
    walkRightUrl: String,
    walkRight2Url: String,
    walkRightMidUrl: String,
    turnRightUrl: String,
    wavingUrl: String
  }

  connect() {
    this.blinkInterval = null
    this.patrolTimeout = null
    this.stepInterval = null
    this.isPatrolling = false
    this.schedulePatrol()

    document.addEventListener("axle:state", this.handleStateEvent.bind(this))
  }

  disconnect() {
    this.cancelPatrol()
    this.stopStepping()
    document.removeEventListener("axle:state", this.handleStateEvent.bind(this))
  }

  urlForState(state) {
    const map = {
      idle: this.idleUrlValue,
      listening: this.listeningUrlValue,
      thinking: this.thinkingUrlValue,
      suggesting: this.suggestingUrlValue,
      success: this.successUrlValue,
      warning: this.warningUrlValue,
      unread: this.suggestingUrlValue,
      walk_left: this.walkLeftUrlValue,
      walk_left2: this.walkLeft2UrlValue,
      walk_left_mid: this.walkLeftMidUrlValue,
      walk_right: this.walkRightUrlValue,
      walk_right2: this.walkRight2UrlValue,
      walk_right_mid: this.walkRightMidUrlValue,
      turn_right: this.turnRightUrlValue,
      waving: this.wavingUrlValue
    }
    return map[state]
  }

  // === Walk Stepping ===
  // Alternates between two walk frames to simulate leg movement

  startStepping(direction) {
    this.stepFrame = 0
    // Full stride cycle: extended1 → mid(parallel) → extended2 → mid(parallel)
    const frames = direction === "left"
      ? ["walk_left", "walk_left_mid", "walk_left2", "walk_left_mid"]
      : ["walk_right", "walk_right_mid", "walk_right2", "walk_right_mid"]

    this.setImage(frames[0])
    this.stepInterval = setInterval(() => {
      this.stepFrame++
      this.setImage(frames[this.stepFrame % frames.length])
    }, 350) // each phase of stride = 350ms
  }

  stopStepping() {
    if (this.stepInterval) {
      clearInterval(this.stepInterval)
      this.stepInterval = null
    }
  }

  // === Patrol System ===

  schedulePatrol() {
    const delay = 15000 + Math.random() * 30000
    this.patrolTimeout = setTimeout(() => {
      if (this.stateValue === "idle" && !this.isPatrolling) {
        this.startPatrol()
      } else {
        this.schedulePatrol()
      }
    }, delay)
  }

  cancelPatrol() {
    if (this.patrolTimeout) {
      clearTimeout(this.patrolTimeout)
      this.patrolTimeout = null
    }
  }

  setImage(state) {
    if (!this.hasImageTarget) return
    const url = this.urlForState(state)
    if (url) this.imageTarget.src = url
  }

  async startPatrol() {
    this.isPatrolling = true
    const el = this.element
    const startRight = 90

    // Phase 1: Walk left with stepping animation (~8s)
    this.startStepping("left")
    el.style.transition = "right 8s linear"
    el.style.right = "calc(100vw - 80px)"

    await this.sleep(8200)
    this.stopStepping()

    // Phase 2: Stop and ponder + thought bubble (~5s)
    this.setImage("thinking")
    el.style.transition = "none"
    this.showThoughtBubble()

    await this.sleep(5000)

    this.hideThoughtBubble()

    // Phase 3: Look around (~1.5s)
    this.setImage("suggesting")

    await this.sleep(1500)

    // Phase 4: Wave while still stopped (~2s)
    this.setImage("waving")

    await this.sleep(2000)

    // Phase 5: Turn to face right (~0.8s)
    this.setImage("turn_right")

    await this.sleep(800)

    // Phase 6: Walk back with stepping animation (~8s)
    this.startStepping("right")
    el.style.transition = "right 8s linear"
    el.style.right = startRight + "px"

    await this.sleep(8200)
    this.stopStepping()

    // Return to idle
    this.setImage("idle")

    this.isPatrolling = false
    this.stateValue = "idle"
    this.schedulePatrol()
  }

  sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms))
  }

  // === Thought Bubble ===

  showThoughtBubble() {
    const message = this.getContextMessage()
    if (!message) return

    // Create bubble if it doesn't exist
    let bubble = document.getElementById('axle-thought')
    if (!bubble) {
      bubble = document.createElement('div')
      bubble.id = 'axle-thought'
      bubble.className = 'axle-thought-bubble'
      document.body.appendChild(bubble)
    }

    // Check for associated link
    const contextEl = document.querySelector('[data-axle-context-link]')
    const link = contextEl?.dataset.axleContextLink

    if (link) {
      bubble.innerHTML = `<span>${message}</span><span class="axle-thought-cta">Let's fix that →</span>`
      bubble.style.cursor = 'pointer'
      bubble.style.pointerEvents = 'auto'
      bubble.onclick = () => { window.location.href = link }
    } else {
      bubble.textContent = message
      bubble.style.cursor = 'default'
      bubble.style.pointerEvents = 'none'
      bubble.onclick = null
    }

    bubble.style.display = 'block'

    // Position near Axle
    const axleRect = this.element.getBoundingClientRect()
    bubble.style.bottom = (window.innerHeight - axleRect.top + 8) + 'px'
    bubble.style.left = axleRect.left + 'px'

    // Animate in
    requestAnimationFrame(() => {
      bubble.style.opacity = '1'
      bubble.style.transform = 'translateY(0) scale(1)'
    })
  }

  hideThoughtBubble() {
    const bubble = document.getElementById('axle-thought')
    if (bubble) {
      bubble.style.opacity = '0'
      bubble.style.transform = 'translateY(8px) scale(0.95)'
      setTimeout(() => { bubble.style.display = 'none' }, 300)
    }
  }

  getContextMessage() {
    // Check for page-specific context first
    const pageContext = document.querySelector('[data-axle-context]')
    if (pageContext) return pageContext.dataset.axleContext

    // Fallback: infer from URL
    const path = window.location.pathname
    const messages = {
      '/coaches': [
        'How\'s the coach doing today?',
        'When was the last time you checked the oil?',
        'Road trip season is coming up...'
      ],
      '/facility/work_orders': [
        'Anything stuck waiting on parts?',
        'Let\'s keep things moving.',
        'Who needs assignment?'
      ],
      '/facility/tech': [
        'What\'s next on the list?',
        'Under target on the last one?',
        'Keep that wrench turning.'
      ],
      '/posts': [
        'Seen any good refits lately?',
        'The community has been active.',
        'Share something about your rig.'
      ],
      '/': [
        'Welcome back.',
        'What can we knock out today?',
        'The network is growing.'
      ]
    }

    // Find matching path
    for (const [key, msgs] of Object.entries(messages)) {
      if (path.startsWith(key) && key !== '/') {
        return msgs[Math.floor(Math.random() * msgs.length)]
      }
    }
    // Default
    const defaults = messages['/']
    return defaults[Math.floor(Math.random() * defaults.length)]
  }

  // === State System ===

  handleStateEvent(event) {
    const { state, duration } = event.detail || {}
    if (state && !this.isPatrolling) this.setState(state, duration)
  }

  setState(state, autoRevertMs) {
    if (this.isPatrolling) return
    this.stateValue = state
    this.setImage(state)

    if (autoRevertMs) {
      setTimeout(() => {
        if (!this.isPatrolling) this.setState("idle")
      }, autoRevertMs)
    }
  }

  stateValueChanged() {
    // CSS animations driven by data-axle-peek-state-value
  }
}
